import _ from 'lodash';
import moment from 'moment';
import { ForwardedRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { fetchHolterEvents, formatEventStrip, updateEventChangeAndNew } from '@/entities/event';
import { KEY_CANCEL, KEY_RECOIL, TYPE_RHYTHM_EVENT_ENUM } from '@/shared/config';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import { useUpdateEffect, usePrevious } from '@/shared/lib/customHooks';
import { toastrError } from '@/shared/lib/toastNotification';
import {
  createChartData,
  createMinutelyHrChartData,
  filterHourlySummaryRanges,
  getMinMaxChart,
  validateEcgDataMap,
} from '@/entities/heart-rate';
import { logError } from '@/shared/lib/apiHelpers';
import {
  selectEventChanges,
  selectEventNews,
  selectReloadHrChart,
  selectSelectedDateValue,
  selectUpdatedSelectedStrip,
  useReportStore,
} from '@/entities/report';
import type { HrChartHandle, HrChartProps } from './hrChart';

/**
 * Feeds the artifact tab's daily heart-rate chart, fetching that day's artifact events and turning the hourly ECG summaries into chart series. It re-fetches whenever the selected date, the reload signal or the ECG render flag changes, resets the chart's current selection on a date change, refreshes silently when local event changes or new events arrive, and exposes an imperative handleClickArrowButton through the forwarded ref.
 *
 * @param props - Study, profile, timezone offset and the ECG data map backing the chart.
 * @param ref - Forwarded handle through which the parent triggers arrow-button navigation.
 * @returns An object carrying keyRecoil, dataChart, updatedSelectedStrip and chartHrRef.
 */
const useHrChart = (
  { timezoneOffset, ecgDataMap, studyId, profileId }: HrChartProps,
  ref: ForwardedRef<HrChartHandle>,
) => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;

  const selectedDateValue = useReportStore(selectSelectedDateValue(keyRecoil));
  const setIsLoadingHrChartAction = useReportStore((s) => s.actions.setIsLoadingHrChart);
  /**
   * Toggles the loading indicator of the artifact heart-rate chart. Passes the TAB_1 key with the new loading flag or updater to the store's setIsLoadingHrChart action.
   *
   * @param v - The next loading flag, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setIsLoadingHrChart = (v: Parameters<typeof setIsLoadingHrChartAction>[1]) =>
    setIsLoadingHrChartAction(keyRecoil, v);
  const eventChanges = useReportStore(selectEventChanges(keyRecoil));
  const eventNews = useReportStore(selectEventNews(keyRecoil));
  const setIsEmptyDataAction = useReportStore((s) => s.actions.setIsEmptyData);
  /**
   * Flags that the heart-rate chart has no data for the selected day. Passes the TAB_1 key with the new empty-data flag or updater to the store's setIsEmptyData action.
   *
   * @param v - The next empty-data flag, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setIsEmptyData = (v: Parameters<typeof setIsEmptyDataAction>[1]) =>
    setIsEmptyDataAction(keyRecoil, v);
  const setCombinedEventsAction = useReportStore((s) => s.actions.setCombinedEvents);
  /**
   * Stores the merged list of server and locally changed artifact events driving the chart. Passes the TAB_1 key with the new combined event array or updater to the store's setCombinedEvents action.
   *
   * @param v - The next combined event array, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setCombinedEvents = (v: Parameters<typeof setCombinedEventsAction>[1]) =>
    setCombinedEventsAction(keyRecoil, v);
  const reloadHrChart = useReportStore(selectReloadHrChart(keyRecoil));
  const updatedSelectedStrip = useReportStore(selectUpdatedSelectedStrip(keyRecoil));
  const isRenderViewerEcg = useReportStore((s) => s.isRenderViewerEcg);

  const chartHrRef = useRef<any>(null);

  const [dataChart, setDataChart] = useState<any>();

  const preSelectedDateValue = usePrevious(selectedDateValue);

  /**
   * Builds the day's heart-rate chart series from the hourly ECG summaries and that day's artifact events. Filters the summaries to the selected day, validates them, derives the average, maximum and minimum minutely arrays, computes the chart bounds, converts each event into start and stop minute indices, then runs createChartData for guides, missing points and per-type event areas; empty or failed paths flag empty data and clear the loading state.
   *
   * @param holterEventsByDate - The artifact events falling inside the selected day.
   * @param needLoading - Whether the triggering fetch showed a loading indicator; when false the chart zooms after drawing.
   * @returns Nothing.
   */
  const formatHrData = (holterEventsByDate: any[] = [], needLoading?: boolean) => {
    try {
      let hasNoData;
      const selectedDateMoment = convertToMomentJs(selectedDateValue, timezoneOffset);
      const { data, channels, samplingFrequency } = ecgDataMap;

      if (data?.length && selectedDateMoment && !_.find(data, (x) => !x.dataUrl)) {
        const startDayMoment = selectedDateMoment.clone().startOf('day');
        const stopDayMoment = selectedDateMoment.clone().endOf('day');
        // *: Filter HR data in date
        // filterHourlySummaryRanges's start/stop (handler.ts, Task 7) are typed number, but every
        // call site here passes a Moment - pre-existing.
        const currentDateData = filterHourlySummaryRanges({
          summaries: ecgDataMap,
          start: startDayMoment,
          stop: stopDayMoment,
          channels,
          samplingFrequency,
          willAddDataRanges: false,
        } as any);

        const isValidCurrentDateData = validateEcgDataMap(currentDateData);
        if (isValidCurrentDateData) {
          const filteredAvgHrArray: any[] = [];
          const filteredMaxHrArray: any[] = [];
          const filteredMinHrArray: any[] = [];
          _.forEach(currentDateData, (item: any) => {
            filteredAvgHrArray.push(item.avgHrs);
            filteredMaxHrArray.push(item.maxHrs);
            filteredMinHrArray.push(item.minHrs);
          });

          // createMinutelyHrChartData's return type (handler.ts, Task 7) is a union of a flat array
          // and an array-of-arrays depending on its args, which the destructure below can't narrow - pre-existing.
          const [avgHrArray, maxHrArray, minHrArray]: any[] = createMinutelyHrChartData({
            hourlySummaries: currentDateData,
            minutelyHrs: filteredAvgHrArray,
            start: startDayMoment,
            stop: stopDayMoment,
            maxMinutelyHrs: filteredMaxHrArray,
            minMinutelyHrs: filteredMinHrArray,
            isEditReportPage: true,
          }) as any;

          if (_.find(avgHrArray, (x) => !_.isNil(x))) {
            // *: All data are null
            let minHr = 0;
            let maxHr = 150;
            const clonedAvgHrArray = [...avgHrArray];
            _.remove(clonedAvgHrArray, (x) => x < 0);
            if (clonedAvgHrArray.length > 0) {
              const minHrTmp = Math.floor(_.min(clonedAvgHrArray) / 10) * 10;
              const maxHrTmp = Math.ceil(_.max(clonedAvgHrArray) / 10) * 10;
              const { min, max } = getMinMaxChart(minHrTmp, maxHrTmp);
              minHr = min || 0;
              maxHr = max || 150;
            }

            const firstEpoch = startDayMoment.valueOf();
            const eventIndicators = _.map(holterEventsByDate, (holterEvent) => {
              const roundStartMoment = moment(holterEvent.start).startOf('minute');
              const roundStopMoment = moment(holterEvent.stop).startOf('minute');
              return {
                ...holterEvent,
                startIndex: (roundStartMoment.valueOf() - firstEpoch) / 1000 / 60,
                stopIndex: (roundStopMoment.valueOf() - firstEpoch) / 1000 / 60,
              };
            });
            const {
              hrs,
              guides,
              missingPoints,
              artifactEventGuides,
              artifactEventAreaPoints,
              afibEventGuides,
              afibEventAreaPoints,
              otherEventGuides,
              otherEventAreaPoints,
              unsaveArtifactEvents,
              unsaveAfibEvents,
              unsaveOtherEvents,
            } = createChartData({
              hrArray: avgHrArray,
              eventIndicators,
              firstEpoch,
              maxHr, //* use max hr to fill null data and draw zone
              maxHrArray,
              minHrArray,
              isEditReportPage: true,
            });

            setDataChart({
              hrs,
              guides,
              missingPoints,
              artifactEventGuides,
              artifactEventAreaPoints,
              afibEventGuides,
              afibEventAreaPoints,
              otherEventGuides,
              otherEventAreaPoints,
              unsaveArtifactEvents,
              unsaveAfibEvents,
              unsaveOtherEvents,
              minHr,
              maxHr,
              eventIndicators,
              firstEpoch,
              isZoomAfterDrawChart: !needLoading,
            });
          } else {
            hasNoData = true;
          }
        } else {
          hasNoData = true;
        }
      } else {
        hasNoData = true;
      }
      if (hasNoData) {
        setIsEmptyData(true);
        setIsLoadingHrChart(false);
      }
    } catch (error) {
      logError('Failed to fetch HR data: ', error);
      toastrError(error.message, 'Error');
      setIsEmptyData(true);
      setIsLoadingHrChart(false);
    }
  };

  /**
   * Loads the artifact rhythm events covering the selected report day and hands them to the chart formatter. Optionally raises the chart loading flag, queries a one-day window under the holter AI cancel key, clears the flag early when the response returns quickly, formats the events, merges pending local event changes and new events into them, stores the combined list and logs any failure.
   *
   * @param needLoading - Whether to show the chart loading indicator while fetching.
   * @returns A promise resolving once the day's events are stored and the chart data is rebuilt.
   */
  const fetchHolterEventsByDate = async (needLoading = true) => {
    if (needLoading) {
      setIsLoadingHrChart(true);
    }
    let downloadedHolterEvents: any[] = [];
    try {
      const start = performance.now();
      const startSearchDate = selectedDateValue;
      const stopSearchDate = moment(selectedDateValue).add(1, 'days')?.toISOString();
      const holterEventsFilter = {
        studyId,
        profileId,
        types: [TYPE_RHYTHM_EVENT_ENUM.ARTIFACT],
        startSearchDate,
        stopSearchDate,
      };
      const holterEvents = await fetchHolterEvents(
        holterEventsFilter,
        0,
        true,
        KEY_CANCEL.API_HOLTER_AI,
      );
      if (performance.now() - start < 100) {
        setIsLoadingHrChart(false);
      }
      if (holterEvents?.events) {
        downloadedHolterEvents = holterEvents.events;
      }
      const dataFormat = formatEventStrip(downloadedHolterEvents);
      const holterEventsByDate = updateEventChangeAndNew(
        dataFormat,
        eventChanges,
        eventNews,
        startSearchDate,
        stopSearchDate,
        [TYPE_RHYTHM_EVENT_ENUM.ARTIFACT],
      );
      setCombinedEvents(holterEventsByDate);
      formatHrData(holterEventsByDate, needLoading);
    } catch (error) {
      logError('Failed to fetch holter events: ', error);
    }
  };

  useEffect(() => {
    // *: Change to other date
    if (selectedDateValue && isRenderViewerEcg) {
      if (preSelectedDateValue !== selectedDateValue) {
        chartHrRef.current.resetCurrentSelect();
      }
      fetchHolterEventsByDate();
    }
  }, [selectedDateValue, reloadHrChart, isRenderViewerEcg]);

  // *: Fetch and re-draw events
  useUpdateEffect(() => {
    if (selectedDateValue && isRenderViewerEcg) {
      fetchHolterEventsByDate(false);
    }
  }, [eventChanges, eventNews, isRenderViewerEcg]);

  useImperativeHandle(ref, () => ({
    handleClickArrowButton: (isNext) => {
      if (chartHrRef.current) {
        chartHrRef.current.handleClickArrowButton(isNext);
      }
    },
  }));

  return {
    keyRecoil,
    dataChart,
    updatedSelectedStrip,
    chartHrRef,
  };
};

export default useHrChart;
